import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { applyNodeChanges, useReactFlow } from "@xyflow/react";
import { applyLaneLayout, applyLiveDragLaneLayout, childBasePosition } from "../layout/lanes";
import { recordSnapOffsets } from "./recordSnapOffsets";
import { PositionManager } from "../positions/PositionManager";
import { isManagedNode } from "../positions/managedNodes";
import { EdgeWaypointManager } from "../edges/EdgeWaypointManager";
import { useSeedAlgoOnReady } from "./useSeedAlgoOnReady";
function buildContext(nodes) {
    return {
        nodes,
        nodeById: new Map(nodes.map((n)=>[
                n.id,
                n
            ]))
    };
}
/**
 * The nodes RF moves during a drag: the explicitly-dragged node plus any others
 * selected at drag time, excluding lane containers. Shared by the drag reflow
 * (recovery set) and the drop commit so the two never drift apart.
 */ function isNodeInDrag(node, primaryId, isLane) {
    return (node.id === primaryId || node.selected === true) && !(isLane?.(node) ?? false);
}
function pointerClientPosition(event) {
    if ("clientX" in event) return {
        x: event.clientX,
        y: event.clientY
    };
    const touch = event.touches[0] ?? event.changedTouches[0];
    return touch ? {
        x: touch.clientX,
        y: touch.clientY
    } : null;
}
/**
 * Overlay the saved layer onto the assembled nodes. Lane containers pass
 * through untouched — see `isManagedNode` for why lanes must never resolve
 * through the manager. `laneLayout` is a required parameter (not an optional
 * predicate) so a lane-capable caller cannot silently omit the exclusion.
 */ function applyPositions(nodes, pm, laneLayout) {
    return nodes.map((n)=>{
        if (!isManagedNode(n, laneLayout?.isLane)) return n;
        const resolved = pm.resolve(n);
        if (resolved.x === n.position.x && resolved.y === n.position.y) return n;
        return {
            ...n,
            position: resolved
        };
    });
}
/**
 * Carry forward `selected: true` from the hook's current `nodes` state into a
 * freshly-built structural snapshot. Without this, every structural rebuild
 * (triggered by a new `source` reference, e.g. a refetch after a mutation)
 * hands RF new node objects with `selected: undefined`, and RF's
 * `adoptUserNodes` treats that as a full replacement — dropping the prior
 * selection and firing a spurious `onSelectionChange([])`.
 *
 * Only `selected: true` is propagated; nodes present in `prev` but not `next`
 * are naturally dropped, so deleting a selected node still yields a correct
 * empty selection event. Returns the same array reference when nothing was
 * merged to keep downstream memos cheap.
 */ export function preserveSelected(next, prev) {
    const prevById = new Map(prev.map((n)=>[
            n.id,
            n
        ]));
    let changed = false;
    const merged = next.map((n)=>{
        const p = prevById.get(n.id);
        if (!p?.selected || n.selected === true) return n;
        changed = true;
        return {
            ...n,
            selected: true
        };
    });
    return changed ? merged : next;
}
/**
 * Drag-commit flush seam. `onNodeDragStop` settles the nodes inside a
 * `setNodes` updater and the persisted positions MUST come from that settled
 * array, so persisted bases match what's rendered. The updater form is
 * mandatory: RF dispatches its final mouseup position change
 * (`dragging: false`, possibly snap-rewritten by the change transform)
 * through `onNodesChange` in the same event, so a direct value computed from
 * a `nodes` closure would clobber that batch — losing the snapped drop
 * position and the `dragging: false` flag.
 *
 * Because the settled array only exists inside the updater, `schedule`
 * stashes it in a ref — an updater side effect, but an idempotent one (React
 * may replay the updater; the last write wins with identical content) — and
 * bumps a tick that the flush effect keys on. The tick is load-bearing:
 * settling can be a referential no-op, so an effect keyed on the nodes array
 * wouldn't fire.
 */ function useDeferredDragCommit(options) {
    const { setNodes, manager, laneLayout, onDragEndRef } = options;
    const pendingRef = useRef(null);
    const [tick, setTick] = useState(0);
    const schedule = useCallback((settle, primaryId)=>{
        setNodes((nds)=>{
            const settled = settle(nds);
            pendingRef.current = {
                settled,
                primaryId
            };
            return settled;
        });
        setTick((t)=>t + 1);
    }, [
        setNodes
    ]);
    useEffect(function flushPendingDragCommit() {
        const pending = pendingRef.current;
        if (!pending) return;
        pendingRef.current = null;
        const { settled, primaryId } = pending;
        const ctx = buildContext(settled);
        // RF mutates `position` on every selected node during a group drag
        // (via the position changes it dispatches through `onNodesChange`), so
        // we just walk the settled nodes and commit each non-lane selected
        // node alongside the explicitly-dragged one.
        const movedNodes = settled.filter((n)=>isNodeInDrag(n, primaryId, laneLayout?.isLane));
        // Single batched commit — keeps `onMove` (and any undo stack behind it)
        // from receiving one entry per moved node during a group drag.
        manager.commitDrags(movedNodes.map((n)=>({
                id: n.id,
                position: n.position
            })), ctx);
        onDragEndRef.current?.(movedNodes.map((n)=>n.id));
    }, [
        tick,
        manager,
        laneLayout,
        onDragEndRef
    ]);
    return schedule;
}
export function useEditableFlow(options) {
    const { adapter, source, savedPositions, onMove, laneLayout, seedAlgoOnReady, decorate, onDragEnd, transformNodeChanges, savedEdgeWaypoints, onUpdateEdgeLayouts } = options;
    // Ref-wrap so `onNodesChange` stays `[]`-stable; a fresh identity each render would
    // cascade through canvas memos.
    const transformNodeChangesRef = useRef(transformNodeChanges);
    transformNodeChangesRef.current = transformNodeChanges;
    const onMoveRef = useRef(onMove ?? adapter.editable?.onMove);
    onMoveRef.current = onMove ?? adapter.editable?.onMove;
    const onDragEndRef = useRef(onDragEnd);
    onDragEndRef.current = onDragEnd;
    const managerRef = useRef(null);
    if (managerRef.current === null) {
        managerRef.current = new PositionManager({
            initialSaved: savedPositions,
            storage: adapter.storage,
            sentinel: adapter.sentinel,
            onDirty: (updates)=>{
                const handler = onMoveRef.current;
                if (handler) void handler(updates);
            }
        });
    }
    const manager = managerRef.current;
    // EdgeWaypointManager mirrors the PositionManager shape for edge routing.
    const onUpdateEdgeLayoutsRef = useRef(onUpdateEdgeLayouts ?? adapter.editable?.onUpdateEdgeLayouts);
    onUpdateEdgeLayoutsRef.current = onUpdateEdgeLayouts ?? adapter.editable?.onUpdateEdgeLayouts;
    const edgeWaypointManagerRef = useRef(null);
    if (edgeWaypointManagerRef.current === null) {
        edgeWaypointManagerRef.current = new EdgeWaypointManager({
            initialSaved: savedEdgeWaypoints,
            onDirty: (updates)=>{
                const handler = onUpdateEdgeLayoutsRef.current;
                if (handler) void handler(updates);
            }
        });
    }
    const edgeWaypointManager = edgeWaypointManagerRef.current;
    const lastSavedWaypointsRef = useRef(savedEdgeWaypoints);
    if (lastSavedWaypointsRef.current !== savedEdgeWaypoints) {
        lastSavedWaypointsRef.current = savedEdgeWaypoints;
        if (savedEdgeWaypoints) edgeWaypointManager.setSaved(savedEdgeWaypoints);
    }
    // Sync `saved` whenever the caller passes a new map identity. This keeps the
    // manager in lockstep with the backend cache (savedPositions are derived
    // from the same query slice that drives the source).
    const lastSavedRef = useRef(savedPositions);
    if (lastSavedRef.current !== savedPositions) {
        lastSavedRef.current = savedPositions;
        manager.setSaved(savedPositions);
    }
    const assembly = useMemo(()=>adapter.build(source), [
        adapter,
        source
    ]);
    // Precomputed edge-derived views handed to `decorate` so consumers don't
    // need to rebuild the assembly to read things like orphan-status. Memoed
    // on `assembly.edges` identity — stable across position-only re-renders.
    const decorateCtx = useMemo(()=>{
        const incomingEdgeTargets = new Set();
        for (const e of assembly.edges)incomingEdgeTargets.add(e.target);
        return {
            edges: assembly.edges,
            incomingEdgeTargets
        };
    }, [
        assembly.edges
    ]);
    const structuralNodes = useMemo(()=>{
        // `manager` is identity-stable; its saved layer is re-synced from
        // `savedPositions` during render (see `lastSavedRef` above). Reading the
        // map here ties the memo to that sync, so a new saved map re-derives the
        // positions even though no other dependency changed identity.
        void savedPositions;
        const decorated = decorate ? assembly.nodes.map((n)=>decorate(n, decorateCtx)) : assembly.nodes;
        const positioned = applyPositions(decorated, manager, laneLayout);
        if (!laneLayout) return positioned;
        return applyLaneLayout(positioned, laneLayout);
    }, [
        assembly,
        decorateCtx,
        manager,
        laneLayout,
        decorate,
        savedPositions
    ]);
    const [nodes, setNodes] = useState(structuralNodes);
    const [prevStructural, setPrevStructural] = useState(structuralNodes);
    // While a drag is in flight, defer structural resets so react-flow's
    // in-progress position updates aren't clobbered by the external mutation.
    // The deferred structuralNodes snapshot is flushed in `onNodeDragStop`.
    const isDraggingRef = useRef(false);
    const pendingStructuralRef = useRef(null);
    if (prevStructural !== structuralNodes) {
        setPrevStructural(structuralNodes);
        if (isDraggingRef.current) {
            pendingStructuralRef.current = structuralNodes;
        } else {
            setNodes(preserveSelected(structuralNodes, nodes));
        }
    }
    // Once-per-mount algo seeding. When the saved layer is ready and any node
    // lacks a saved position, run the adapter's layout preset and persist its
    // output for those nodes. Lane containers are derived by `applyLaneLayout`
    // on every render and must not be persisted — their ids are synthetic.
    const initialBuildRef = useRef(()=>adapter.build(source));
    initialBuildRef.current = ()=>adapter.build(source);
    const initialSavedHasRef = useRef((id)=>savedPositions.has(id));
    initialSavedHasRef.current = (id)=>savedPositions.has(id);
    const { isSeeding } = useSeedAlgoOnReady({
        enabled: Boolean(seedAlgoOnReady),
        assembly,
        adapter,
        manager,
        laneLayout,
        initialSavedHas: (id)=>initialSavedHasRef.current(id),
        buildInitialAssembly: ()=>initialBuildRef.current()
    });
    // Seed-on-appear: when a synthetic node first materializes mid-session
    // (e.g. a `+` aggregator after a fork, or an end node after detaching a
    // successor), the adapter's sentinel produces a derived position from its
    // predecessor. Persist it on first sight so subsequent moves of the
    // predecessor don't keep dragging the synthetic with them. We only persist
    // once per id (tracked in `seededIdsRef`) and only when the position's
    // BASE coordinates differ from the manager's `{0,0}` fallback. The check
    // must be growth-aware: `structuralNodes` positions are post-lane-layout RF
    // coordinates, which the growth offsets shift, so comparing a lane child's
    // raw position against `{0,0}` would never match once any lane has grown —
    // the junk `{0,0}` base would be persisted, pinning the global leftGrowth
    // (every lane box shifted left) forever after reload.
    const seededIdsRef = useRef(new Set());
    useEffect(function seedOnAppear() {
        if (isSeeding) return;
        const isLane = laneLayout?.isLane;
        const laneById = new Map(isLane ? structuralNodes.filter((n)=>isLane(n)).map((n)=>[
                n.id,
                n
            ]) : []);
        const updates = [];
        for (const n of structuralNodes){
            if (!isManagedNode(n, isLane)) continue;
            if (manager.hasSaved(n.id)) continue;
            if (seededIdsRef.current.has(n.id)) continue;
            const parent = n.parentId ? laneById.get(n.parentId) : undefined;
            const base = parent ? childBasePosition(n, parent, laneLayout?.getUpGrowth) : n.position;
            if (base.x === 0 && base.y === 0) continue;
            updates.push({
                id: n.id,
                position: n.position
            });
            seededIdsRef.current.add(n.id);
        }
        if (updates.length > 0) {
            manager.seedSaved(updates, buildContext(structuralNodes));
        }
    }, [
        structuralNodes,
        manager,
        laneLayout,
        isSeeding
    ]);
    // Per-node deltas the change transform applied to in-drag positions (see
    // `recordSnapOffsets`); consumed by `onNodeDrag` when building drag targets.
    const snapOffsetsRef = useRef(new Map());
    const onNodesChange = useCallback((changes)=>{
        const transform = transformNodeChangesRef.current;
        const next = transform ? transform(changes) : changes;
        if (transform) recordSnapOffsets(changes, next, snapOffsetsRef.current);
        setNodes((nds)=>applyNodeChanges(next, nds));
    }, []);
    const rf = useReactFlow();
    const dragSessionRef = useRef(null);
    // Latest committed nodes for the drag-start snapshot — a ref so
    // `onNodeDragStart` keeps a stable identity (a `nodes` dependency would give
    // it a fresh identity every drag frame, cascading through canvas memos).
    const nodesRef = useRef(nodes);
    nodesRef.current = nodes;
    const onNodeDragStart = useCallback((event, node)=>{
        if (laneLayout?.isLane(node)) return;
        isDraggingRef.current = true;
        // Anchor the drag to the pointer: capture the dragged lane children (the
        // same predicate `onNodeDragStop` commits with) and their absolute
        // positions.
        dragSessionRef.current = null;
        snapOffsetsRef.current.clear();
        const client = pointerClientPosition(event);
        if (laneLayout && client) {
            const current = nodesRef.current;
            const laneById = new Map(current.filter((n)=>laneLayout.isLane(n)).map((n)=>[
                    n.id,
                    n
                ]));
            const startAbs = new Map();
            for (const n of current){
                if (!n.parentId || !isNodeInDrag(n, node.id, laneLayout.isLane)) continue;
                const parent = laneById.get(n.parentId);
                if (!parent) continue;
                startAbs.set(n.id, {
                    x: parent.position.x + n.position.x,
                    y: parent.position.y + n.position.y
                });
            }
            dragSessionRef.current = {
                pointerStart: rf.screenToFlowPosition(client, {
                    snapToGrid: false
                }),
                startAbs
            };
        }
    }, [
        laneLayout,
        rf
    ]);
    const onNodeDrag = useCallback((event, node)=>{
        if (!laneLayout) return;
        if (laneLayout.isLane(node)) return;
        const session = dragSessionRef.current;
        const client = pointerClientPosition(event);
        if (!session || !client) return;
        const pointer = rf.screenToFlowPosition(client, {
            snapToGrid: false
        });
        const deltaX = pointer.x - session.pointerStart.x;
        const deltaY = pointer.y - session.pointerStart.y;
        const targets = new Map();
        // Fold the change-transform's snap adjustment (recorded in
        // `onNodesChange` this same pointer event) into the pointer-anchored
        // target — otherwise the lane pass would re-derive the dragged card from
        // the raw pointer and discard helper-line magnetism.
        for (const [id, start] of session.startAbs){
            const snap = snapOffsetsRef.current.get(id);
            targets.set(id, {
                x: start.x + deltaX + (snap?.x ?? 0),
                y: start.y + deltaY + (snap?.y ?? 0)
            });
        }
        setNodes((nds)=>applyLiveDragLaneLayout(nds, laneLayout, targets));
    }, [
        laneLayout,
        rf
    ]);
    const scheduleDragCommit = useDeferredDragCommit({
        setNodes,
        manager,
        laneLayout,
        onDragEndRef
    });
    const onNodeDragStop = useCallback((_event, node)=>{
        if (laneLayout?.isLane(node)) return;
        // Settle: re-run the canonical lane layout. The drag layout maintained
        // the growth invariants (`rf = base + growth` on both axes) every
        // frame, so this is visually a no-op — it absorbs RF's final unsolved
        // mouseup frame and restores the lane boxes the drag pass kept frozen.
        // The settle runs inside a `setNodes` updater and the commit comes from
        // the settled array — see `useDeferredDragCommit` for why both are
        // mandatory.
        scheduleDragCommit((nds)=>laneLayout ? applyLaneLayout(nds, laneLayout) : nds, node.id);
        isDraggingRef.current = false;
        dragSessionRef.current = null;
        snapOffsetsRef.current.clear();
        const pending = pendingStructuralRef.current;
        if (pending) {
            pendingStructuralRef.current = null;
            setNodes((nds)=>preserveSelected(pending, nds));
        }
    }, [
        laneLayout,
        scheduleDragCommit
    ]);
    return {
        nodes,
        edges: assembly.edges,
        onNodesChange,
        onNodeDragStart,
        onNodeDrag,
        onNodeDragStop,
        positionManager: manager,
        edgeWaypointManager,
        isSeeding
    };
}

//# sourceMappingURL=useEditableFlow.js.map